<template>
  <tab-item-template>
    <template #toolbar-items>
      <v-spacer/>
      <toolbar-inline>
        <v-text-field
            v-model="searchText"
            clearable
            placeholder="検索"/>
      </toolbar-inline>
    </template>

    <el-table
        class="no-padding"
        row-key="id"
        :data="processOperationsView"
        :span-method="buildTableSpan"
        :height="tabItemBounds.height"
        default-expand-all
        border>
      <el-table-column
          min-width="300"
          label="サービス構成モデル名 / プロセス構成モデル名"
          prop="name"
          class-name="no-padding-excluded">
        <template #header>
          サービス構成モデル名 / <br>プロセス構成モデル名
        </template>
      </el-table-column>
      <el-table-column
          min-width="500"
          label="説明"
          prop="description"
          class-name="no-padding-excluded">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.description"
                placeholder="未設定"
                multi-line>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          v-for="pot in processOperationTypeItems"
          :key="`operation-type-${pot.name}`"
          :column-key="`operation-type-${pot.name}`"
          :label="pot.label"
          :prop="pot.name"
          width="50"
          align="center">
        <template #default="{row}">
          <table-inline>
            <readonly-checkbox :value="row.operationTypes.includes(pot.name)"></readonly-checkbox>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          v-if="isEditable"
          width="40"
          align="center"
          column-key="operations">
        <template #default="{row}">
          <icon-btns>
            <icon-btn
                v-if="row.header"
                type="edit"
                :to="{
                  name: alterRouteName('ServiceComponentModel/processOperations'),
                  params: {
                    dictionaryId: dictionary.id,
                    serviceComponentModelId: row.id
                  },
                  query: {
                    selectedDictionary: $route.query.selectedDictionary,
                    selectedDataComponentModel: dataComponentModel.id
                  }
                }">
            </icon-btn>
          </icon-btns>
        </template>
      </el-table-column>
    </el-table>
  </tab-item-template>
</template>

<script>
import PathInfoMixin from "@/router/PathInfoMixin";
import TableInline from "@/components/atoms/TableInline.vue";
import {mapGetters, mapMutations} from "vuex";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import IconBtn from "@/components/molecules/IconBtn.vue";
import ToolbarInline from "@/components/atoms/ToolbarInline.vue";
import {PROCESS_OPERATION_OPERATION_TYPES} from "@/constants";
import BetterSet from "betterset";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import IconBtns from "@/components/molecules/IconBtns";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";

export default {
  name: 'process-operation-list',
  components: {
    TextFieldLike,
    IconBtns,
    ReadonlyCheckbox,
    ToolbarInline,
    IconBtn,
    TabItemTemplate,
    TableInline
  },
  mixins: [PathInfoMixin],
  props: {
    dictionary: Object,
    dataComponentModel: Object,
  },
  inject: [
    "tabItemBounds"
  ],
  data() {
    return {
      searchText: null,
      processOperationTypeItems: ['create', 'read', 'update', 'delete'].map(t => ({
        name: t,
        label: PROCESS_OPERATION_OPERATION_TYPES[t],
      })),
    };
  },
  computed: {
    ...mapGetters({
      entities: 'hds/entities'
    }),
    serviceComponentModels() {
      return this.entities.serviceComponentModel.find();
    },
    processOperations() {
      const _e = this.entities;
      return _e.processOperation
          .find({dataComponentModelId: this.dataComponentModel.id})
          .map(po => {
            const processComponentModel = _e.processComponentModel.findOne({id: po.processComponentModelId});
            const dataComponentModel = _e.dataComponentModel.findOne({id: po.dataComponentModelId});
            return {
              ...po,
              name: processComponentModel.name,
              description: processComponentModel.description,
              processComponentModel,
              dataComponentModel
            }
          })
          .sort((a, b) => a.index - b.index);
    },
    processOperationsFiltered() {
      if (this.searchText) {
        return this.processOperations
            .filter(d =>
                d.description.indexOf(this.searchText) !== -1 ||
                d.processComponentModel.name.indexOf(this.searchText) !== -1 ||
                d.dataComponentModel.name.indexOf(this.searchText) !== -1);
      }
      return this.processOperations;
    },
    processOperationsView() {
      return this.serviceComponentModels
        .map(scm => ({
          ...scm,
          header: true,
          operationTypes: [],
          children: this.entities.processComponentModel
              .find({serviceComponentModelId: scm.id})
              .map(pcm => ({
                ...pcm,
                operationTypes: this.collectOperationTypes(pcm.id)
              }))
              .filter(pcm => pcm.operationTypes.length > 0)
        }))
        .filter(scm => scm.children.length > 0);

    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar',
    }),
    /**
     * 指定されたプロセス実装IDに関連するプロセス手順実装の区分を収集します
     *
     * @param {string} processComponentModelId プロセス構成モデルID
     * @return {(keyof typeof PROCESS_OPERATION_OPERATION_TYPES)[]} プロセス手順実装の区分の配列
     */
    collectOperationTypes(processComponentModelId) {
      const result = new BetterSet();
      this.processOperationsFiltered.forEach(po => {
        if(po.processComponentModel.id === processComponentModelId) {
          result.add(po.operationType);
        }
      });
      return result.asArray();
    },
    /**
     * テーブルの結合状態を生成します。
     * ヘッダ行の場合に全列を結合します。
     *
     * @param row 行データ
     * @param column 列データ
     * @param columnIndex 列インデックス
     * @return {{colspan: number, rowspan: number}} 行・列のセルの結合数
     */
    buildTableSpan({row, column, columnIndex}) {
      if(row.header) {
        if(columnIndex === 1) {
          return {
            rowspan: 1,
            colspan: this.processOperationTypeItems.length + 1
          };
        } else if(PROCESS_OPERATION_OPERATION_TYPES[column.property]) {
          return {
            rowspan: 0,
            colspan: 0
          };
        }
      }
    },
  }
}
</script>
